import type {
  CustomerInfoData,
  CustomerUsageTotals,
  CustomerAllData,
  ResourceMetricData,
  ResourceMetricType,
} from '@/lib/types/Customer';
import type { IAuthenticatedCPUser } from '@/lib/types/Auth';
import type { SubAccountEditFormValues } from './schemas/subAccountSchemas';

const BYTES_PER_GB = 1073741824; // 1024^3
const SECONDS_PER_DAY = 86400;

export interface AppLicenseInfo {
  /** Master account's total app license limit (MAX_SAFE_INTEGER if unlimited). */
  masterAppLimit: number;
  /** Apps currently in use by the master account itself. */
  masterAppsInUse: number;
  /** Apps currently in use by the specified sub-account. */
  subAccountAppsInUse: number;
  /** Total apps in use family-wide (master + ALL sub-accounts). */
  totalAppsInUse: number;
  /** Sum of app limits allocated to other sub-accounts (excluding the specified one). */
  otherSubsAllocatedLimits: number;
  /** Remaining app licenses available for assignment: masterAppLimit - totalAppsInUse (family-wide). */
  appsAvailableForAssignment: number;
}

export class AccountUtils {
  /**
   * Resolve the parent (master) cId for billing API calls.
   * Billing always uses the master account cId, never a sub-account cId.
   */
  static resolveParentCId(
    customerInfo: CustomerInfoData | null,
    authData: IAuthenticatedCPUser | null,
  ): number | null {
    if (!customerInfo || !authData) return null;

    const userCId = authData.cpuser.core.cId;
    const masterCId = customerInfo.customer.cfg.cId;
    const isFromParentAccount = userCId === masterCId;

    if (isFromParentAccount) {
      return masterCId;
    }

    // User is from a sub-account — use parentCId from the master customer config
    // Use || instead of ?? because backend may return 0 instead of null for "no parent"
    return customerInfo.customer.cfg.parentCId || masterCId;
  }

  /**
   * Check if the logged-in user belongs to the parent/master account.
   */
  static isFromParentAccount(
    authData: IAuthenticatedCPUser | null,
    customerInfo: CustomerInfoData | null,
  ): boolean {
    if (!authData || !customerInfo) return false;
    return authData.cpuser.core.cId === customerInfo.customer.cfg.cId;
  }

  /**
   * Whole days until the trial expires (legacy App.component daysToExpiration
   * parity): positive = full days left, 0 = expires today, negative = expired.
   * `nowSecs` is injected for deterministic testing.
   */
  static trialDaysRemaining(expiresSecs: number, nowSecs: number): number {
    return Math.floor((expiresSecs - nowSecs) / SECONDS_PER_DAY);
  }

  /** Compact trial-status copy for the header notice. */
  static trialStatusMessage(daysRemaining: number): string {
    if (daysRemaining > 0) {
      return `Trial expires in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`;
    }
    return daysRemaining === 0 ? 'Trial expires today' : 'Trial has expired';
  }

  /** Trial notice urgency color: blue, orange within 3 days, red today/expired. */
  static trialUrgencyColor(daysRemaining: number): 'blue' | 'orange' | 'red' {
    if (daysRemaining <= 0) return 'red';
    if (daysRemaining <= 3) return 'orange';
    return 'blue';
  }

  /** Convert bytes to GB for display. Clamps negative values to 0. */
  static bytesToGB(bytes: number): number {
    if (!isFinite(bytes) || bytes < 0) return 0;
    return bytes / BYTES_PER_GB;
  }

  /** Convert GB input to bytes for API transmission. */
  static gbToBytes(gb: number): number {
    return Math.round(gb * BYTES_PER_GB);
  }

  /** Format bytes as a human-readable GB string (e.g. "146.2 GB"). */
  static formatBytesAsGB(bytes: number, decimals = 1): string {
    const gb = AccountUtils.bytesToGB(bytes);
    return `${gb.toFixed(decimals)} GB`;
  }

  /** Calculate usage percentage (0-100). Returns null if no limit or invalid input. */
  static usagePercent(used: number, limit: number | null): number | null {
    if (limit === null || limit === 0) return null;
    if (!isFinite(used) || used < 0) return 0;
    return Math.min(100, (used / limit) * 100);
  }

  /**
   * Get progress bar color based on usage percentage.
   * Green: under 75%, Yellow: 75-100% (approaching), Red: over 100% (exceeded).
   */
  static usageColor(percent: number): string {
    if (percent > 100) return 'red';
    if (percent >= 75) return 'yellow';
    return 'green';
  }

  /**
   * Sum the CURRENT-month resource-metric value (bytes) of a given type across
   * the supplied metrics. The backend leaves cfg.quota.limits.{storage,transfer}
   * BytesUsed at 0 and expects the dashboard to derive live usage from the
   * resourceMetrics buckets (parity with legacy
   * ResourceMetricsUtil.getCurrentMonth{Storage,DataTransfer}Bytes). The current
   * bucket is the month interval containing `nowSeconds` (start inclusive, end
   * exclusive). `nowSeconds` is injected for deterministic testing.
   */
  static currentMonthResourceUsage(
    metrics: ResourceMetricData[],
    type: ResourceMetricType,
    nowSeconds: number,
  ): number {
    let total = 0;
    for (const m of metrics) {
      if (
        m.resourceMetricType === type &&
        m.resourceMetricInterval === 'month' &&
        m.start <= nowSeconds &&
        m.end > nowSeconds
      ) {
        total += m.value;
      }
    }
    return total;
  }

  /**
   * Calculate app license info for a sub-account.
   * Reusable across sub-account form validation, grid display, and helper text.
   *
   * Availability is usage-based and FAMILY-WIDE: masterAppLimit minus every
   * app in use under the master (its own plus all sub-accounts'). This is a
   * deliberate divergence from legacy validateAppCfgLimit, which counted only
   * the master's own apps plus the current sub-account's and therefore
   * overstated availability on masters with app-holding siblings — the
   * displayed "available for assignment" number is the number enforcement
   * uses, so it must mean what it says.
   */
  static getAppLicenseInfo(
    masterAccount: CustomerAllData,
    subAccounts: { account: CustomerAllData; appCfgsInUse: number }[],
    usageTotals: CustomerUsageTotals[],
    currentSubCId: number | null,
  ): AppLicenseInfo {
    const masterAppLimit = masterAccount.cfg.quota.features.app.appCfgLimit ?? Number.MAX_SAFE_INTEGER;

    const masterAppsInUse = usageTotals.find(t => t.cId === masterAccount.cfg.cId)?.appCfgs ?? 0;

    const currentSub = currentSubCId
      ? subAccounts.find(s => s.account.cfg.cId === currentSubCId)
      : null;
    const subAccountAppsInUse = currentSub?.appCfgsInUse ?? 0;

    const allSubsAppsInUse = subAccounts.reduce((sum, s) => sum + s.appCfgsInUse, 0);
    const totalAppsInUse = masterAppsInUse + allSubsAppsInUse;

    const otherSubsAllocatedLimits = subAccounts
      .filter(s => s.account.cfg.cId !== currentSubCId)
      .reduce((sum, s) => sum + (s.account.cfg.quota.features.app.appCfgLimit ?? 0), 0);

    const appsAvailableForAssignment = masterAppLimit - totalAppsInUse;

    return {
      masterAppLimit,
      masterAppsInUse,
      subAccountAppsInUse,
      totalAppsInUse,
      otherSubsAllocatedLimits,
      appsAvailableForAssignment,
    };
  }

  /**
   * Map a stored sub-account to edit-form values.
   *
   * The API omits unset optional fields entirely (same wire behavior as the
   * media list endpoints), so every optional read normalizes undefined
   * explicitly. A missing limit means "unlimited" and must map to null:
   * mapping it through a number path would seed an unrendered field with an
   * invalid value (Zod rejects, submit silently no-ops with no visible error
   * — the wfs "cannot save sub-account" bug) and, for storage/transfer,
   * would overwrite unlimited with a hard 0 GB limit on the next save.
   */
  static toSubAccountEditFormValues(account: CustomerAllData): SubAccountEditFormValues {
    const cfg = account.cfg;
    const limits = cfg.quota.limits;
    const app = cfg.quota.features.app;
    return {
      label: cfg.label,
      isActive: cfg.customerStateType === 'active',
      storageLimitGB: limits.storageBytesLimit != null ? this.bytesToGB(limits.storageBytesLimit) : null,
      transferLimitGB: limits.transferBytesLimit != null ? this.bytesToGB(limits.transferBytesLimit) : null,
      appCfgLimit: app.appCfgLimit ?? 0,
      allowDvr: cfg.quota.features.account.allowDvr ?? null,
      videostreamLimit: limits.videostreamLimit ?? null,
      allowWeb: app.allowWeb ?? false,
      allowAndroid: app.allowAndroid ?? false,
      allowIos: app.allowIos ?? false,
      allowRoku: app.allowRoku ?? false,
      allowAppletv: app.allowAppletv ?? false,
      allowFiretv: app.allowFiretv ?? false,
      allowAndroidtv: app.allowAndroidtv ?? false,
    };
  }

  /**
   * Validate a sub-account app-limit value. Returns an error message, or
   * undefined when the value may be saved.
   *
   * The rules (stored limit = allowance consciously granted):
   * 1. Unchanged values always save (grandfather exemption — accounts
   *    provisioned before app-license enforcement must remain editable;
   *    an unchanged allocation cannot worsen the license position).
   * 2. The limit can never be set below the sub-account's apps in use.
   * 3. An INCREASE may not exceed the licenses available for assignment
   *    (family-wide, usage-based). Delta-based on purpose: it blocks
   *    "regularizing" a grandfathered stored-0 up to its in-use count when
   *    no allowance exists (a functional no-op that would corrupt the
   *    allocation ledger and inflate the total-allocation sum until it
   *    wedges every other sub-account edit), and it caps partial increases
   *    at what is actually available (legacy's binary check accepted any
   *    size of increase as long as availability was merely positive).
   *    Reductions are always license-free. Legacy also allowed raising up
   *    to in-use regardless of availability — deliberately dropped.
   * 4. Total allocated limits across sub-accounts may not exceed the
   *    master's limit (allocation-ledger backstop).
   * Create mode passes `persistedLimit = null` (baseline 0 — a new
   * sub-account's whole limit is an increase; no exemption).
   */
  static validateAppLimitChange(
    newLimit: number,
    persistedLimit: number | null,
    info: AppLicenseInfo,
  ): string | undefined {
    if (persistedLimit !== null && newLimit === persistedLimit) return undefined;

    const {
      subAccountAppsInUse,
      appsAvailableForAssignment,
      otherSubsAllocatedLimits,
      masterAppLimit,
    } = info;

    if (newLimit < subAccountAppsInUse) {
      return `Cannot set the limit below the number of apps currently in use (${subAccountAppsInUse}).`;
    }
    const increase = newLimit - (persistedLimit ?? 0);
    if (increase > 0 && increase > appsAvailableForAssignment) {
      return appsAvailableForAssignment > 0
        ? (
          `Only ${appsAvailableForAssignment} App License${appsAvailableForAssignment === 1 ? '' : 's'} available for assignment. ` +
          `Master account limit: ${masterAppLimit}.`
        )
        : (
          `No App Licenses available. You must increase your App Licenses. ` +
          `Master account limit: ${masterAppLimit}.`
        );
    }
    if (otherSubsAllocatedLimits + newLimit > masterAppLimit) {
      return `Total sub-account app limits (${otherSubsAllocatedLimits + newLimit}) exceed master account limit (${masterAppLimit}).`;
    }
    return undefined;
  }
}
